import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { addressName, formatAddress, SavedAddress } from '../../data/places';
import { colors, hairline, radii, screenPadding, spacing } from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

type Props = {
  visible: boolean;
  /**
   * The book to choose from.
   *
   * Passed in rather than read from `data/places`: that array is seed data
   * that nothing writes to, so a sheet reading it directly could never offer
   * the address the customer saved a minute ago in the location flow.
   */
  addresses: SavedAddress[];
  /** The address currently in use, so it can be marked. */
  current: string;
  onClose: () => void;
  /** After the sheet has fully gone - see `BottomSheet`'s `onClosed`. */
  onClosed?: () => void;
  onSelect: (address: SavedAddress) => void;
  /** Starts the location flow for an address not on file. */
  onAddNew: () => void;
  /**
   * Starts the same flow, but from a GPS fix rather than from the map alone.
   *
   * Offered here rather than inside the location screen, where it used to be
   * the second thing a customer saw. "I am standing at the address" and "I am
   * adding somewhere else" are the two ways this task starts, so they belong
   * side by side, before any screen opens.
   */
  onUseCurrentLocation: () => void;
  /**
   * Opens the location flow on an address already saved.
   *
   * Takes the address so a caller can prefill the form. Nothing does yet -
   * the flow has no way to load one - so today this behaves the same as
   * adding, and the argument is what makes that fixable without changing
   * every call site.
   */
  onEdit: (address: SavedAddress) => void;
};

/**
 * Choosing where the work happens, as a sheet over the cart.
 *
 * A sheet rather than a screen: picking from a short list is a single decision
 * on the way to checkout, and pushing a screen would lose the basket's scroll
 * position for the sake of two rows.
 *
 * "Add address" is first and not last. Someone opening this sheet with nothing
 * on file - the case that forces it open - has no list to read, and putting
 * the only useful control under an empty heading makes them look for it.
 */
function AddressSheet({
  visible,
  addresses,
  current,
  onClose,
  onSelect,
  onAddNew,
  onUseCurrentLocation,
  onEdit,
  onClosed,
}: Props) {
  return (
    <BottomSheet visible={visible} onClose={onClose} onClosed={onClosed}>
      <View style={styles.header}>
        <Text variant="titleLg" style={styles.headerTitle}>
          Select an address
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          hitSlop={10}
        >
          <Icon
            name="plus"
            size={18}
            color={colors.inkMuted}
            strokeWidth={2.2}
            style={styles.closeGlyph}
          />
        </Pressable>
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Add address"
        onPress={onAddNew}
        style={({ pressed }) => [styles.add, pressed ? styles.pressed : null]}
      >
        <Icon name="plus" size={18} color={colors.primary} strokeWidth={2.2} />
        <Text variant="subtitle" color={colors.primary} style={styles.addText}>
          Add Address
        </Text>
        <Icon name="chevronRight" size={16} color={colors.primary} />
      </Pressable>

      {/*
        Directly under "Add Address", because it is the other half of the same
        choice: type it, or use where I am. Drawn plainer than the row above -
        no tinted ground - so the two read as a primary action and its
        shortcut rather than as two equal buttons competing.
      */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Use my current location"
        onPress={onUseCurrentLocation}
        style={({ pressed }) => [
          styles.locate,
          pressed ? styles.pressed : null,
        ]}
      >
        <Icon name="target" size={18} color={colors.primary} />
        <View style={styles.locateText}>
          <Text variant="labelSm" color={colors.primary}>
            Use current location
          </Text>
          <Text variant="caption" color={colors.inkMuted}>
            Find my address with GPS
          </Text>
        </View>
        <Icon name="chevronRight" size={16} color={colors.primary} />
      </Pressable>

      <ScrollView
        contentContainerStyle={styles.list}
        showsVerticalScrollIndicator={false}
      >
        <Text variant="eyebrow" color={colors.inkMuted} style={styles.listLabel}>
          Saved addresses
        </Text>

        {addresses.length === 0 ? (
          <Text variant="caption" color={colors.inkMuted} style={styles.empty}>
            No addresses yet. Add one above and we will know where to send
            somebody.
          </Text>
        ) : null}

        {addresses.map((address, index) => {
          const line = formatAddress(address);
          const name = addressName(address);
          const active = line === current;
          /*
           * An address with no pin cannot be booked against - the server reads
           * the serviceable city off the coordinates - so choosing it would
           * only fail later, at checkout. Tapping it opens the location flow
           * instead, which is the one thing that can fix it.
           */
          const pinned = Boolean(address.coords);

          return (
            <Pressable
              /*
               * The server's row id, which is what `toSavedAddress` puts in
               * `place.id` and what `SavedAddressesScreen` already keys on.
               *
               * This used to be `${label}-${place.title}`, and that is not
               * unique: two addresses filed under Home in the same locality
               * both key as "Home-Telephone Nagar", which React reports as a
               * duplicate key and then renders as one row or the wrong one.
               * Someone with a home and a parent's flat in the same area saw
               * exactly that.
               *
               * The index is a fallback, not the key: `placeFromPin` defaults
               * `id` to the literal 'detected', so an address picked from the
               * map but not yet saved to the server shares that string with
               * every other one. Position separates those; the id keeps the
               * saved rows stable when the list reorders.
               */
              key={
                address.place.id === 'detected'
                  ? `detected-${index}`
                  : address.place.id
              }
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={
                pinned
                  ? `${name}. ${line}`
                  : `${name}. ${line}. No location set - tap to set it on the map`
              }
              onPress={() => (pinned ? onSelect(address) : onEdit(address))}
              style={({ pressed }) => [
                styles.card,
                active ? styles.cardOn : null,
                pressed ? styles.pressed : null,
              ]}
            >
              <View style={styles.cardMark}>
                <Icon name="pin" size={16} color={colors.primary} />
              </View>

              <View style={styles.cardBody}>
                {/* The same words the cart's row uses, so the two read as the
                    same fact seen twice rather than two different labels. */}
                <View style={styles.cardHead}>
                  <Text variant="eyebrow" color={colors.inkMuted}>
                    Service at
                  </Text>

                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Edit ${name}`}
                    onPress={() => onEdit(address)}
                    hitSlop={10}
                  >
                    <Text variant="labelSm" color={colors.primary}>
                      Edit
                    </Text>
                  </Pressable>
                </View>

                <View style={styles.cardName}>
                  <Text variant="subtitle">{name}</Text>
                  {active ? (
                    <Icon
                      name="check"
                      size={15}
                      color={colors.primary}
                      strokeWidth={2.4}
                    />
                  ) : null}
                </View>

                <Text variant="caption" color={colors.inkMuted}>
                  {line}
                </Text>
                {address.note ? (
                  <Text variant="caption" color={colors.inkMuted}>
                    {address.note}
                  </Text>
                ) : null}
                {/* Said on the row rather than discovered at checkout. */}
                {pinned ? null : (
                  <View style={styles.warn}>
                    <Icon name="pin" size={13} color={colors.danger} />
                    <Text variant="caption" color={colors.danger}>
                      No location set - tap to set it on the map
                    </Text>
                  </View>
                )}
                {/*
                  Saved, but not somewhere we can serve yet.

                  Drawn in muted ink rather than danger red, unlike the
                  missing-pin warning above it: that one is a fault the
                  customer can fix by tapping, this one is a fact about where
                  we operate and there is nothing for them to do about it.
                  Red would read as "this address is wrong".

                  Only for an explicit `false`. An address the platform never
                  judged leaves the field undefined, and that must draw
                  nothing - see `SavedAddress.serviceable`.
                */}
                {address.serviceable === false ? (
                  <View style={styles.warn}>
                    <Icon name="clock" size={13} color={colors.inkMuted} />
                    <Text variant="caption" color={colors.inkMuted}>
                      Coming soon - we don't serve this area yet
                    </Text>
                  </View>
                ) : null}
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },
  headerTitle: {
    flex: 1,
  },
  // A rotated plus - the icon set has no cross glyph.
  closeGlyph: {
    transform: [{ rotate: '45deg' }],
  },

  add: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginHorizontal: screenPadding,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceAccent,
  },
  addText: {
    flex: 1,
  },
  /* No tinted ground, unlike `add` above it - see the render for why the two
     are weighted differently. */
  locate: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginHorizontal: screenPadding,
    marginTop: spacing.xs,
    padding: spacing.md,
    borderRadius: radii.md,
  },
  locateText: {
    flex: 1,
  },

  list: {
    paddingBottom: spacing.md,
  },
  listLabel: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },
  empty: {
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.md,
  },
  warn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: 2,
  },
  card: {
    flexDirection: 'row',
    gap: spacing.md,
    marginHorizontal: screenPadding,
    marginBottom: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  cardOn: {
    borderColor: colors.primary,
  },
  cardMark: {
    width: 32,
    height: 32,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  cardBody: {
    flex: 1,
    gap: 2,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardName: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default AddressSheet;
